import type { SlotGameStore, ReelsetComponent as ReelsetComponentType } from '@gamzix/slot_core';

import { CoinAnimationComponent } from '../../coinAnimation/src';
import { transitionBgPrefab, secondTransitionBgPrefab } from '../prefabs/transition-bg.prefab';

class BonusGameComponent extends SlotGame.src.component.BonusGameComponent {
    protected tsAnim;
    protected introPopupAnim;
    protected outroPopupAnim;
    protected outroPopupMaxWinAnim;
    protected outroMaxWinPopupContainer;
    protected outroPopupContainer;
    protected introTextContainer;
    protected isSkipped: boolean = false;

    static events = {
        START_WIN_GRAND: 'BonusGameComponent.START_WIN_GRAND',
        END_WIN_GRAND: 'BonusGameComponent.END_WIN_GRAND',
        UPDATE_COUNTER: 'BonusGameComponent.UPDATE_COUNTER',
        START_GRAND_WIN_COUNTER: 'BonusGameComponent.START_GRAND_WIN_COUNTER',
        STOP_GRAND_WIN_COUNTER: 'BonusGameComponent.STOP_GRAND_WIN_COUNTER',
        START_INTRO_POPUP: 'BonusGameComponent.START_INTRO_POPUP',
        START_HIDE_INTRO_POPUP: 'BonusGameComponent.START_HIDE_INTRO_POPUP',
        END_INTRO_POPUP: 'BonusGameComponent.END_INTRO_POPUP',

        START_OUTRO_POPUP: 'BonusGameComponent.START_OUTRO_POPUP',
        START_HIDE_OUTRO_POPUP: 'BonusGameComponent.START_HIDE_OUTRO_POPUP',
        END_OUTRO_POPUP: 'BonusGameComponent.END_OUTRO_POPUP',

        OUTRO_POPUP_START_COUNT: 'BonusGameComponent.OUTRO_POPUP_START_COUNT',
        OUTRO_POPUP_END_COUNT: 'BonusGameComponent.OUTRO_POPUP_END_COUNT'
    };

    override onGameReady() {
        super.onGameReady();
        this.outroPopupAnim = game.tree.findByName('bonus_game.outro_popup_spine');
        this.outroPopupMaxWinAnim = game.tree.findByName('bonus_game.outro_popup_zero_spine');
        this.outroMaxWinPopupContainer = game.tree.findByName(
            'bonus_game.wrapper_container.popup_outro_max_win_container'
        );
        this.outroPopupContainer = game.tree.findByName('bonus_game.wrapper_container.popup_outro_container');
        this.introTextContainer = game.tree.findByName('bonus_game.intro_popup_container.skip_text');
    }

    constructor(config) {
        super(config);

        this.status_checkers = {
            ...super.getStatusCheckers(),

            isEnglishLang: () => (<SlotGameStore>game.store).language === 'en',
        };

        this.text_checkers = {
            ...super.getTextCheckers(),

            GET_TOTAL_WIN_NUMBER: () => this.store.toFormatMoney(this.store.gameData.bonusGame.win),
        };
    }
    setElementVisibility(element, isVisible) {
        if (!element) return;
        if (element.options.visible !== isVisible) {
            element.options.visible = isVisible;
            element.updateViewOptions();
        }
    }

    async showStartPopup() {
        game.emit('BonusGameComponent.START_INTRO_POPUP');

        const container = game.tree.findByName('bonus_game.wrapper_container.popup_intro_container');
        const fader = game.tree.findByName('bonus_game.wrapper_container.popup_intro_fader');
        container.options.visible = true;

        game.utils.GameUtils.playAlphaAnimation(container, { from: 0, to: 1, time: 300, timeWait: 0 });

        if (!this.tsAnim || !this.introPopupAnim) {
            const isTsanimLoaded = game.builder.isResourcesLoaded({ type: 'SpineByName', name: 'transition_bg' });
            const isSecondTransitionisLoading = game.builder.isResourcesLoaded({
                type: 'SpineByName',
                name: 'BG_transition',
            });

            if (isTsanimLoaded && isSecondTransitionisLoading) {
                const transitionContainer = game.tree.findByName('bonus_game.wrapper_transition_container');

                this.tsAnim = transitionBgPrefab(game);
                this.introPopupAnim = secondTransitionBgPrefab(game);

                transitionContainer.addChild(this.introPopupAnim);
                transitionContainer.addChild(this.tsAnim);

                await this.runIntroTransition();
            } else {
                // If need test, place this code in if after await this.runIntroTransition(),
                // and comment call of await this.runIntroTransition();
                fader.options.visible = true;
                await game.utils.delay(2000);
            }
        } else {
            fader.options.visible = false;
            await this.runIntroTransition();
        }

        game.emit(BonusGameComponent.events.START_HIDE_INTRO_POPUP);
        await game.utils.GameUtils.playAlphaAnimation(container, { from: 1, to: 0, time: 300, timeWait: 0 });
        game.utils.GameUtils.playAlphaAnimation(this.introTextContainer, { from: 1, to: 0, time: 300, timeWait: 0 });
        this.setElementVisibility(this.introPopupAnim, false);
        container.options.visible = false;
        this.isSkipped = false;
        game.emit('BonusGameComponent.END_INTRO_POPUP', {});
    }

    async runIntroTransition(): Promise<void> {
        const animName = this.status_checkers.isPortrait() ? 'transition_desktop' : 'transition_portrait';
        this.setElementVisibility(this.tsAnim, true);
        this.tsAnim.stop();
        this.tsAnim.playAnimation(animName, false);

        await game.utils.delay(1160);

        this.setElementVisibility(this.introPopupAnim, true);
        this.introPopupAnim.stop();
        this.introPopupAnim.playAnimation('appearance', false);

        game.utils.GameUtils.playAlphaAnimation(this.introTextContainer, { from: 0, to: 1, time: 300, timeWait: 0 });
        game.emit('UIComponent.SET_PARENT_GROUP_FOR_SKIPPING', { parentGroupName: 'TOP' });
        await game.utils.cancelableDelay(1200, () => (this.isSkipped = true));

        if (!this.isSkipped) {
            this.introPopupAnim.playAnimation('idle', true);
            await game.utils.cancelableDelay(10000);
        }
        game.emit('UIComponent.SET_PARENT_GROUP_FOR_SKIPPING', { parentGroupName: 'OVERLAY' });

        this.introPopupAnim.playAnimation('dissappearance', false);
    }

    async showFinishPopup() {
        game.emit('BonusGameComponent.START_OUTRO_POPUP');
        game.emit('UIComponent.SET_PARENT_GROUP_FOR_SKIPPING', { parentGroupName: 'TOP' });
        if (this.isGrandJackpot) {
            await this.showOutroMaxWinPopup();
        } else {
            await this.showOutroPopup();
        }

        game.emit('BonusGameComponent.END_OUTRO_POPUP', {});
        game.emit('UIComponent.SET_PARENT_GROUP_FOR_SKIPPING', { parentGroupName: 'OVERLAY' });

        // if (!this.gameData.isFreespinGamePopupsInProgress) game.emit("BonusGameComponent.END_OUTRO_POPUP.PlayMainSound");
    }

    async showOutroPopup() {
        const totalWin = game.tree.findByName('bonus_game.outro_popup_container.total_win_value');
        const winText = game.tree.findByName('bonus_game.outro_popup_container.bonus_win_text');
        const bonusText = game.tree.findByName('bonus_game.outro_popup_container.bonus_game_text');

        this.outroPopupAnim.spine.slotContainers[
            this.outroPopupAnim.spine.slotContainers.length - 3
        ].children[0].addChild(winText);
        this.outroPopupAnim.spine.slotContainers[
            this.outroPopupAnim.spine.slotContainers.length - 3
        ].children[0].addChild(bonusText);

        this.outroPopupContainer.options.visible = true;

        totalWin.options.text = this.store.toFormatMoney(1);
        totalWin.updateViewOptions();

        this.setElementVisibility(this.outroPopupAnim, true);
        game.utils.GameUtils.playAlphaAnimation(this.outroPopupContainer, { from: 0, to: 1, time: 300, timeWait: 0 });
        this.outroPopupAnim.stop();
        this.outroPopupAnim.playAnimation('BG_appearance', false);

        const playFinalAnimation = async () => {
            await game.utils.delay(960);
            this.outroPopupAnim.playAnimation('BG_idle', true);
        };
        playFinalAnimation();

        await game.utils.CancelableAnimation.create(
            async () => {
                await game.utils.delay(100);
                game.emit(BonusGameComponent.events.OUTRO_POPUP_START_COUNT);
                if (this.store.gameData.bonusGame.win >= this.store.gameData.bet * 10) {
                    game.emit('WinPresentation.SHOW_WIN_PRESENTATION', { mode: 'money_middle' });
                }
                await game.utils.GameUtils.playCancelableCounter(
                    totalWin,
                    1,
                    this.store.gameData.bonusGame.win,
                    3000,
                    createjs.Ease.sineOut
                );
                game.emit(BonusGameComponent.events.OUTRO_POPUP_END_COUNT);
                game.emit('WinPresentation.STOP_GENERATION');
                await game.utils.delay(10000);
            },
            () => {
                game.emit(BonusGameComponent.events.OUTRO_POPUP_END_COUNT);
                game.emit('WinPresentation.STOP_GENERATION');
            }
        );

        await game.utils.delay(500);
        game.emit(BonusGameComponent.events.START_HIDE_OUTRO_POPUP);
        game.emit('WinPresentation.HIDE_PRESENTATION');
        this.outroPopupAnim.playAnimation('BG_dissappearance', true);
        await game.utils.GameUtils.playAlphaAnimation(this.outroPopupContainer, {
            from: 1,
            to: 0,
            time: 500,
            timeWait: 0,
        });
        this.outroPopupContainer.options.visible = false;
    }

    async showOutroMaxWinPopup() {
        this.outroMaxWinPopupContainer.options.visible = true;
        this.setElementVisibility(this.outroPopupMaxWinAnim, true);
        game.utils.GameUtils.playAlphaAnimation(this.outroMaxWinPopupContainer, {
            from: 0,
            to: 1,
            time: 300,
            timeWait: 0,
        });
        this.outroPopupMaxWinAnim.stop();
        this.outroPopupMaxWinAnim.playAnimation('BG_appearance', false);
        await game.utils.delay(960);

        this.outroPopupMaxWinAnim.playAnimation('BG_idle', true);

        game.emit(BonusGameComponent.events.OUTRO_POPUP_START_COUNT);

        await game.utils.CancelableAnimation.create(
            async () => {
                game.emit(BonusGameComponent.events.OUTRO_POPUP_END_COUNT);
                await game.utils.delay(10000);
            },
            () => {
                game.emit(BonusGameComponent.events.OUTRO_POPUP_END_COUNT);
            }
        );

        await game.utils.delay(500);
        game.emit(BonusGameComponent.events.START_HIDE_OUTRO_POPUP);

        this.outroPopupMaxWinAnim.playAnimation('BG_dissappearance', true);
        await game.utils.GameUtils.playAlphaAnimation(this.outroMaxWinPopupContainer, {
            from: 1,
            to: 0,
            time: 500,
            timeWait: 0,
        });
        this.outroMaxWinPopupContainer.options.visible = false;
    }

    onRestoreBonusGame() {
        super.onRestoreBonusGame();
        this.store.gameData.visualWinAmount = this.store.gameData.winAmount - this.store.gameData.bonusGame.win;
    }

    override async onExitBonusGame(): Promise<void> {
        await Promise.race([game.oncePromise(CoinAnimationComponent.events.ANIMATE_FINISHED)]);
        await super.onExitBonusGame();
    }

    get reelset(): ReelsetComponentType {
        return <any>game.component.reelset;
    }
}

export { BonusGameComponent };

//@ts-ignore
Game.src.component.BonusGameComponent = BonusGameComponent;
